import { FilterDataType, FilterOperators } from '@/constant/filter';
import { formatDate } from './datetime';
import moment from 'moment';

/**
 * To string của giá trị bất kỳ
 * 
 * @param {any} v - GIá trị đầu vào
 * @returns {string} - chuỗi string theo format của querystring
 */
const toString = (v) => `'''${v}'''`;

/**
 * Chuyển đổi giá trị ngày tháng thành chuỗi điều kiện SQL dựa trên các toán tử và định dạng cho trước.
 * 
 * @param {string} field - Tên của trường trong cơ sở dữ liệu.
 * @param {any} v - Giá trị đầu vào có thể là số, đối tượng Date, chuỗi hoặc mảng.
 * @param {Object} opr - Đối tượng chứa mã của toán tử.
 * @returns {string} - Chuỗi điều kiện SQL đã được định dạng.
 */
const toDate = (field, v, opr) => {
  if (moment(v).isValid()) {
    const date = formatDate(v, 'YYYY-MM-DD');
    switch (opr.code) {
      case FilterOperators.LessThenOrEqual.code:
        return `{${field}} <= #${date} 23:59:59#`;
      case FilterOperators.LessThen.code:
        return `{${field}} < #${date}#`;
      case FilterOperators.GreaterThen.code:
        return `{${field}} > #${date} 23:59:59#`;
      case FilterOperators.GreateThenOrEqual.code:
        return `{${field}} >= #${date}#`;
      case FilterOperators.NotEqual.code:
        return `({${field}} < #${date}# OR {${field}} > #${date} 23:59:59#)`;
      default:
        return `({${field}} >= #${date}# AND {${field}} <= #${date} 23:59:59#)`;
    }
  } else if (Array.isArray(v) && v.length === 2) {
    const date1 = formatDate(v[0], 'YYYY-MM-DD');
    const date2 = formatDate(v[1], 'YYYY-MM-DD');
    switch (opr.code) {
      case FilterOperators.Between.code:
        return `({${field}} >= #${date1}# AND {${field}} <= #${date2} 23:59:59#)`;
      case FilterOperators.NotBetween.code:
        return `({${field}} < #${date1}# OR {${field}} > #${date2} 23:59:59#)`;
      default:
        return '';
    }
  }

  return '';
};

/**
 * Tạo chuỗi điều kiện SQL cho khoảng thời gian dựa trên giá trị ngày tháng.
 * 
 * @param {string} field - Tên của trường trong cơ sở dữ liệu.
 * @param {Date[]} v - Mảng chứa hai giá trị ngày tháng.
 * @returns {string} - Chuỗi điều kiện SQL đã được định dạng cho khoảng thời gian.
 */
const toDateRange = (field, v) => {
  if (v.length < 2) return '';

  const date1 = formatDate(v[0], 'YYYY-MM-DD');
  const date2 = formatDate(v[1], 'YYYY-MM-DD');
  return `({${field}} >= #${date1}# AND {${field}} <= #${date2} 23:59:59#)`;
};

/**
 * Xây dựng chuỗi truy vấn SQL từ các điều kiện lọc.
 * 
 * @param {Object[]} conditions - Mảng các đối tượng điều kiện lọc.
 * @returns {string} - Chuỗi truy vấn SQL đã được xây dựng.
 */
export const buildQueryStringFromFilter = (conditions) => {
  const formattedValue = (elem) => {
    switch (elem.valueType) {
      case FilterDataType.BOOLEAN:
        return elem.value;
      case FilterDataType.INT:
        return Number.parseInt(elem.value);
      case FilterDataType.DOUBLE:
        return Number.parseFloat(elem.value);
      case FilterDataType.DATE:
        return toDate(elem.field.code, elem.value, elem.opr);
      case FilterDataType.DATE_RANGE:
        return toDateRange(elem.field.code, elem.value);
      case FilterDataType.STRING:
        return toString(elem.value);
      case FilterDataType.ARRAY_STRING: {
        const valuesInStr = elem.value.reduce((res, e, idx) => {
          return idx ? res + ', ' + toString(e) : toString(e);
        }, '');
        return `[${valuesInStr}]`;
      }
      case FilterDataType.ARRAY_INT: {
        const valuesInStr = elem.value.join(',');
        return `[${valuesInStr}]`;
      }
    }
  };

  return conditions.reduce((res, elem, i) => {
    if (i === 0) elem.prefix = '';

    const value = formattedValue(elem);
    if (!value) return res;

    return [FilterDataType.DATE, FilterDataType.DATE_RANGE].includes(elem.valueType)
      ? res + elem.prefix + value
      : res + elem.prefix + `{${elem.field.code}} ` + elem.opr.code + ' ' + value;
  }, '');
};
